import React, { useEffect, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import Exercise from '../../components/exercise';
import { getExerciseById, validateCheckerStep } from '../../actions/homework';
import { onPlayVideo } from '../../actions/media-player';
import { showModal } from '../../actions/modal';
import Button from '../../components/button';
import { exerciseTypes } from 'z-js-commons';
import { useWindowSize } from '../../lib/hooks';
import { resetCorrectionsStatus, showExerciseCorrection } from '../../actions/status';
import { setProfileName } from '../../actions/application';
import './index.css';

const Container = styled.div`
    padding: ${(props) => (props.scrollBarVisible ? 'var(--space-unit) var(--space-unit) var(--space-unit) 0' : '0')};
    height: 100vh;

    scrollbar-color: var(--blue500) var(--gray200);
`;

const ScrollingHelper = styled.div`
    overflow-y: auto;
    height: 100%;
`;

const ExerciseContainer = styled.div`
    min-height: 384px;
    padding: ${(props) =>
        props.scrollBarVisible
            ? 'calc(var(--space-unit) * 2) calc(var(--space-unit) * 2) calc(var(--space-unit) * 2) calc(var(--space-unit) * 3)'
            : 'calc(var(--space-unit) * 3)'};
`;

const ButtonContainer = styled.div`
    display: flex;
    align-items: center;
    height: 56px;
    background-color: var(--gray50);
    padding-left: calc(var(--space-unit) * 2);
`;

const ExercisePreviewPage = ({
    correctionsStatus,
    exercises,
    getExerciseById,
    onPlayVideo,
    resetCorrectionsStatus,
    showExerciseCorrection,
    showModal,
    validateCheckerStep,
    videos,
    setProfileName,
}) => {
    const { exerciseId } = useParams();

    const ref = useRef(null);
    const dispatch = useDispatch();

    const [answers, setAnswers] = useState([]);
    const [hintBoxOpen, setHintBoxOpen] = useState(false);
    const [scrollBarVisible, setScrollBarVisible] = useState(false);

    const { innerWidth } = useWindowSize();
    const isMobile = innerWidth < 340;

    const handleResize = () => {
        setScrollBarVisible(ref.current && ref.current.scrollHeight > ref.current.clientHeight);
    };

    useEffect(() => {
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        // ogni apertura dell'anteprima riparte senza correzioni
        resetCorrectionsStatus();

        const initData = async () => {
            getExerciseById(exerciseId);
        };

        initData().catch(() => {});

        handleResize();
    }, [exerciseId, getExerciseById, resetCorrectionsStatus]);

    useEffect(() => {
        handleResize();
    }, [exercises]);

    useEffect(() => {
        dispatch(setProfileName('Anteprima incorporata'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!exercises || !Array.isArray(exercises) || exercises.length === 0 || exercises[0] === null) {
        return <></>;
    }

    const exerciseType = exerciseTypes.getExerciseTypeFromName(exercises[0].typeLabel);
    const correctionStatus = correctionsStatus.find((cs) => cs.exerciseId === exercises[0].id);

    return (
        <Container scrollBarVisible={scrollBarVisible}>
            <ScrollingHelper ref={ref}>
                <ExerciseContainer scrollBarVisible={scrollBarVisible}>
                    <Exercise
                        exercise={exercises[0]}
                        answers={answers}
                        onAnswer={(_, answers) => setAnswers([...answers])}
                        correctable={true}
                        onCorrection={() => {}}
                        onPlayVideo={onPlayVideo}
                        profile={{}}
                        showModal={showModal}
                        videos={videos}
                        homeworkTerminated={false}
                        correctionStatus={correctionStatus}
                        hintBoxIsOpen={hintBoxOpen}
                        toggleHintBox={() => setHintBoxOpen(!hintBoxOpen)}
                        validateCheckerStep={validateCheckerStep}
                        ignoreBeforeUnload={true}
                    />
                </ExerciseContainer>
                {!correctionStatus && exerciseType !== exerciseTypes.OPEN_ANSWER && exerciseType !== exerciseTypes.SHARED_TEXT && (
                    <ButtonContainer>
                        <Button type="tertiary" size="small" onClick={() => showExerciseCorrection(exercises[0], answers)}>
                            {isMobile ? 'Correggi' : 'Correggi esercizio'}
                        </Button>
                    </ButtonContainer>
                )}
            </ScrollingHelper>
        </Container>
    );
};

ExercisePreviewPage.propTypes = {
    correctionsStatus: PropTypes.array,
    exercises: PropTypes.array,
    getExerciseById: PropTypes.func,
    onPlayVideo: PropTypes.func,
    resetCorrectionsStatus: PropTypes.func,
    showExerciseCorrection: PropTypes.func,
    showModal: PropTypes.func,
    validateCheckerStep: PropTypes.func,
    videos: PropTypes.array,
    setProfileName: PropTypes.func,
};

const mapStateToProps = (state) => {
    return {
        correctionsStatus: state.status.correctionsStatus,
        exercises: state.homework.exercises,
        videos: state.mediaPlayer.videos,
    };
};

const actionCreators = {
    getExerciseById,
    onPlayVideo,
    resetCorrectionsStatus,
    showExerciseCorrection,
    showModal,
    validateCheckerStep,
    setProfileName,
};

export default connect(mapStateToProps, actionCreators)(ExercisePreviewPage);
